import React, { useEffect } from 'react';
import { motion } from 'motion/react';
import { Calendar, User, ArrowLeft, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const fadeUp = {
  hidden: { opacity: 0, y: 30, transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] } },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.25, 1, 0.5, 1] } }
};

export default function AIChatbots() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="pt-32 pb-24 min-h-screen bg-black text-white relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-purple-900/20 rounded-full blur-[120px] pointer-events-none z-0" />
      
      <div className="max-w-4xl w-full mx-auto px-4 sm:px-8 relative z-10">
        
        <motion.div variants={fadeUp} initial="hidden" animate="visible" className="mb-8">
          <Link to="/blogs" className="inline-flex items-center text-sm font-body text-white/50 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Blogs
          </Link>
        </motion.div>

        <motion.div variants={fadeUp} initial="hidden" animate="visible" className="mb-12">
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <span className="px-3 py-1 text-xs font-medium rounded-full bg-white/10 text-white font-body border border-white/5">
              Customer Service
            </span>
            <div className="flex items-center gap-1.5 text-xs text-white/40 font-body">
              <Calendar className="w-3.5 h-3.5" />
              May 05, 2026
            </div>
            <div className="flex items-center gap-1.5 text-xs text-white/40 font-body">
              <User className="w-3.5 h-3.5" />
              EsyAI Team
            </div>
          </div>
          
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading text-white leading-[1.1] mb-6">
            How AI Chatbots Transform Your Customer Service
          </h1>
          
          <p className="text-xl text-white/60 font-body font-light leading-relaxed">
            Learn how AI-powered chatbots are helping UK businesses deliver faster, smarter, and more cost-effective customer service, 24 hours a day, 7 days a week.
          </p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.25, 1, 0.5, 1] }}
          className="w-full aspect-[16/9] md:aspect-[21/9] rounded-3xl overflow-hidden mb-16 border border-white/10 liquid-glass shadow-2xl relative"
        >
          <img 
            src="/blogs/blog-6-new.png" 
            alt="AI Chatbot Interface" 
            className="w-full h-full object-cover opacity-80"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent pointer-events-none" />
        </motion.div>

        <motion.article 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4, ease: [0.25, 1, 0.5, 1] }}
          className="prose prose-invert prose-lg max-w-none font-body text-white/70 prose-headings:font-heading prose-headings:text-white prose-a:text-purple-400 hover:prose-a:text-purple-300 prose-p:leading-loose"
        >
          <p className="lead text-xl text-white/90">
            Customer expectations have never been higher. People want instant answers, personalised responses, and seamless support at any hour of the day. Meeting those expectations with a purely human team is expensive, exhausting, and ultimately impossible at scale. AI chatbots change everything.
          </p>
          
          <p>
            At EsyAI, we design and deploy intelligent AI chatbots for businesses across the UK, built to handle real customer conversations and deliver genuine results. Here is why more businesses are making the switch, and why yours should too.
          </p>

          <h2 className="text-2xl mt-12 mb-6">What Is an AI Chatbot?</h2>
          <p>
            An AI chatbot is a software application that uses artificial intelligence and natural language processing to understand and respond to customer messages in real time. Unlike the clunky, script-based bots of the past, modern AI chatbots can hold natural conversations, understand context, answer complex questions, and take meaningful actions like processing a refund, booking an appointment, or updating an account.
          </p>

          <h2 className="text-2xl mt-12 mb-6">1. Always On, Always Instant</h2>
          <p>
            Your customers do not only have questions between 9am and 5pm. An AI chatbot answers queries instantly, at any hour, on any day, including weekends and bank holidays. This means no missed leads, no frustrated customers waiting for a reply, and no calls going to voicemail.
          </p>

          <h2 className="text-2xl mt-12 mb-6">2. Handle High Volumes Without Additional Cost</h2>
          <p>
            During peak periods, such as product launches, sales events, or seasonal spikes, customer enquiries can multiply overnight. A human team struggles to cope and quality suffers. An AI chatbot handles 1,000 simultaneous conversations just as effectively as it handles 10, with no drop in quality and no increase in cost.
          </p>

          <h2 className="text-2xl mt-12 mb-6">3. Resolve the Majority of Tickets Automatically</h2>
          <p>
            A well-trained AI chatbot can resolve 70 to 85% of customer queries without any human involvement. Common questions about orders, delivery times, pricing, returns, account details, and product information can all be handled instantly and accurately.
          </p>
          <ul className="list-disc pl-6 space-y-2 my-6 marker:text-purple-500">
            <li>Order tracking and status updates</li>
            <li>FAQ responses and product information</li>
            <li>Appointment booking and rescheduling</li>
            <li>Refund and returns processing</li>
            <li>Account queries and password resets</li>
          </ul>

          <h2 className="text-2xl mt-12 mb-6">4. Personalised at Scale</h2>
          <p>
            Modern AI chatbots connect to your CRM and customer data, allowing them to greet customers by name, reference their purchase history, and tailor responses to their specific situation. This level of personalisation at scale is something a human team simply cannot deliver consistently.
          </p>

          <h2 className="text-2xl mt-12 mb-6">5. Reduce Customer Service Costs Significantly</h2>
          <p>
            Deploying an AI chatbot typically reduces customer service operating costs by 50 to 60% compared to an equivalent human team. For growing businesses, this is the difference between scaling profitably and being held back by headcount costs.
          </p>

          <h2 className="text-2xl mt-12 mb-6">6. Collect Valuable Customer Insights</h2>
          <p>
            Every conversation your chatbot has is a data point. Over time, AI chatbots reveal patterns in what customers are asking, what they are confused about, what objections they raise before purchasing, and where your product or service can be improved.
          </p>

          <h2 className="text-2xl mt-12 mb-6">7. Seamless Handoff to Human Agents</h2>
          <p>
            The best AI chatbot deployments know their limits. When a conversation becomes too complex or emotionally sensitive, the chatbot hands off to a human agent smoothly, passing the full conversation history so the customer never has to repeat themselves.
          </p>

          <hr className="my-12 border-white/10" />

          <h2 className="text-2xl mb-6">The Bottom Line</h2>
          <p>
            Your customers want fast, accurate, helpful support. AI chatbots deliver exactly that, at a fraction of the cost of a full human team, around the clock, every day of the year.
          </p>

          <div className="not-prose mt-12 p-8 liquid-glass rounded-3xl border border-white/10 bg-gradient-to-br from-purple-900/20 to-transparent">
            <h3 className="text-xl font-heading text-white mb-3">Ready to transform your customer service?</h3>
            <p className="text-white/70 mb-6 text-base font-body">
              Our AI chatbots are custom-built for your business. We train them on your products, services, policies, and tone of voice.
            </p>
            <Link 
              to="/contact" 
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-black font-medium text-sm rounded-full hover:bg-white/90 transition-colors font-body"
            >
              Talk to EsyAI <ArrowUpRight className="w-4 h-4 ml-2" />
            </Link>
          </div>

        </motion.article>
      </div>
    </div>
  );
}
